import { INTERPRETER_BASE_URL } from "../config/runtime";

/**
 * Service for interacting with the document and file-related A2A API endpoints.
 */

/**
 * Helper to make JSON-RPC calls to the A2A endpoint.
 * @param {string} method - The RPC method name.
 * @param {object} paramsData - The data for the 'params' object.
 * @param {string} userId - The user ID.
 * @returns {Promise<any>} - The result from the API call.
 */
async function a2aApiCall(method, paramsData, userId) {
  console.log(
    `[documentService] Making A2A API call - method: ${method}, paramsData:`,
    paramsData,
  );

  const requestBody = {
    jsonrpc: "2.0",
    id: crypto.randomUUID(),
    method: method,
    params: {
      metadata: { user_id: userId },
      message: { parts: [{ type: "data", data: paramsData }] },
    },
  };

  console.log(
    `[documentService] A2A request body:`,
    JSON.stringify(requestBody, null, 2),
  );

  const response = await fetch(`${INTERPRETER_BASE_URL}/api/a2a`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    console.error(
      `[documentService] A2A API call failed with status: ${response.status}`,
    );
    throw new Error(`API call failed with status: ${response.status}`);
  }

  const data = await response.json();
  console.log(`[documentService] A2A API response:`, data);

  if (data.error) {
    console.error(`[documentService] A2A API error:`, data.error);
    throw new Error(data.error.message || `API error in method: ${method}`);
  }

  const result = data.result?.artifact?.parts?.[0]?.data;
  console.log(`[documentService] Extracted result from A2A response:`, result);
  return result;
}

/** Placeholder or invalid chunk IDs that don't exist in the DB. */
function isPlaceholderChunkId(chunkId) {
  if (!chunkId || typeof chunkId !== "string") return true;
  const s = chunkId.trim();
  if (s === "no_chunk_id_available") return true;
  if (/^0{8}-0{4}-0{4}-0{4}-0{12}$/i.test(s)) return true;
  if (/^chunk_\d+_no_uuid$/i.test(s) || /^linked_chunk_\d+_no_uuid$/i.test(s))
    return true;
  // Reject LLM-fabricated IDs that aren't valid UUIDs (e.g. "safety_002", "step_3")
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)
  )
    return true;
  return false;
}

export const documentService = {
  isPlaceholderChunkId,

  /**
   * Gets the filename for a given chunk ID.
   * @param {string} chunkId - The UUID of the chunk.
   * @param {string} userId - The user ID.
   * @returns {Promise<string|null>} The filename or null if not found.
   */
  async getFilenameFromChunk(chunkId, userId) {
    console.log(`[documentService] Getting filename for chunk ID: ${chunkId}`);
    const result = await a2aApiCall(
      "GetFilenameFromChunk",
      { chunk_id: chunkId },
      userId,
    );
    const filename = result?.filename || null;
    console.log(
      `[documentService] Retrieved filename: ${filename} for chunk ID: ${chunkId}`,
    );
    return filename;
  },

  /**
   * Gets document info and chunk location for scroll/highlight.
   * Uses GET /api/a2a/chunk-location so the call is direct and response is { filename, content, source }.
   * Falls back to A2A GetChunkLocation if GET fails (e.g. 404).
   * @param {string} chunkId - The UUID of the chunk.
   * @param {string} userId - The user ID.
   * @returns {Promise<{filename: string, content?: string, source: Array<{pageIndex: number, startPercentage: number, endPercentage: number}>}>}
   */
  async getChunkLocation(chunkId, userId) {
    const base =
      (INTERPRETER_BASE_URL && String(INTERPRETER_BASE_URL).trim()) || "";
    const path = "/api/a2a/chunk-location";
    const query = `chunk_id=${encodeURIComponent(chunkId)}${userId != null ? `&user_id=${encodeURIComponent(userId)}` : ""}`;
    const url = base
      ? `${base.replace(/\/+$/, "")}${path}?${query}`
      : `${path}?${query}`;
    try {
      const res = await fetch(url, {
        method: "GET",
        credentials: "same-origin",
      });
      if (res.ok) {
        const data = await res.json();
        return {
          filename: data.filename ?? "",
          content: data.content ?? "",
          source: Array.isArray(data.source) ? data.source : [],
          fileId: data.file_id ?? null,
        };
      }
    } catch (e) {
      // fall through to A2A fallback
    }
    const result = await a2aApiCall(
      "GetChunkLocation",
      { chunk_id: chunkId },
      userId,
    );
    const filename = result?.filename ?? "";
    const content = result?.content ?? "";
    const source = Array.isArray(result?.source) ? result.source : [];
    const fileId = result?.file_id ?? null;
    return { filename, content, source, fileId };
  },

  /**
   * Gets a signed URL for a given filename.
   * @param {string} filename - The name of the file.
   * @param {string} userId - The user ID.
   * @returns {Promise<string|null>} The signed URL or null if not found.
   */
  async getSignedUrl(filename, userId) {
    console.log(
      `[documentService] Getting signed URL for filename: ${filename}, user_id: ${userId}`,
    );
    const result = await a2aApiCall(
      "GetSignedUrl",
      {
        file_name: filename,
        user_id: userId.toString(),
      },
      userId,
    );
    const signedUrl = result?.signed_url || null;
    console.log(
      `[documentService] Retrieved signed URL: ${signedUrl ? "SUCCESS" : "FAILED"} for filename: ${filename}`,
    );
    return signedUrl;
  },

  /**
   * Gets a signed URL optimized for viewing in browser (without download headers).
   * @param {string} filename - The name of the file.
   * @param {string} userId - The user ID.
   * @returns {Promise<string|null>} The signed URL for viewing or null if not found.
   */
  async getSignedUrlForViewing(fileId, userId) {
    const result = await a2aApiCall(
      "GetSignedUrl",
      { file_id: fileId.toString(), user_id: userId.toString(), view_mode: true },
      userId,
    );
    const signedUrl = result?.signed_url || null;
    console.log(
      `[documentService] Retrieved viewing URL: ${signedUrl ? "SUCCESS" : "FAILED"} for file_id: ${fileId}`,
    );
    if (signedUrl) {
      console.log(`[documentService] View URL details: ${signedUrl}`);
      // Check if URL has the expected inline headers
      if (signedUrl.includes("response-content-disposition=inline")) {
        console.log(`[documentService] ✓ URL has inline disposition header`);
      } else {
        console.warn(
          `[documentService] ⚠️ URL missing inline disposition header - may still download`,
        );
      }
    }
    return signedUrl;
  },

  /**
   * Gets a signed URL for viewing a file from the snapshot bucket.
   * @param {string} filePath - The full file path in the snapshot bucket (e.g., "snapshot_name/gcs/user_id/filename.pdf")
   * @returns {Promise<string|null>} The signed URL for viewing or null if not found.
   */
  async getSnapshotSignedUrlForViewing(filePath) {
    console.log(`[documentService] 📁 Getting signed URL for snapshot file`);
    console.log(`[documentService] 📁 Original filePath: ${filePath}`);
    console.log(
      `[documentService] 📁 filePath type: ${typeof filePath}, length: ${filePath?.length}`,
    );

    // Extract the file path from the full GCS path if needed
    let cleanPath = filePath;
    if (filePath && filePath.startsWith("gs://rtwx_snapshots_development/")) {
      cleanPath = filePath.replace("gs://rtwx_snapshots_development/", "");
      console.log(
        `[documentService] 📁 Removed gs:// prefix, cleanPath: ${cleanPath}`,
      );
    } else {
      console.log(
        `[documentService] 📁 No gs:// prefix found, using filePath as-is: ${cleanPath}`,
      );
    }

    const requestParams = {
      file_path: cleanPath,
      bucket_name: "rtwx_snapshots_development", // Specify snapshot bucket
      view_mode: true,
    };
    console.log(
      `[documentService] 📁 A2A request params:`,
      JSON.stringify(requestParams, null, 2),
    );

    try {
      // Note: Snapshots don't require user_id in metadata since they use bucket_name
      const result = await a2aApiCall("GetSignedUrl", requestParams, null);
      console.log(`[documentService] 📁 A2A API response received:`, result);

      const signedUrl = result?.signed_url || null;
      if (signedUrl) {
        console.log(
          `[documentService] 📁 ✅ SUCCESS - Retrieved snapshot viewing URL`,
        );
        console.log(
          `[documentService] 📁 Signed URL length: ${signedUrl.length}`,
        );
        console.log(
          `[documentService] 📁 Signed URL preview: ${signedUrl.substring(0, 150)}...`,
        );
      } else {
        console.error(
          `[documentService] 📁 ❌ FAILED - No signed_url in response`,
        );
        console.error(
          `[documentService] 📁 Response structure:`,
          JSON.stringify(result, null, 2),
        );
      }
      return signedUrl;
    } catch (error) {
      console.error(
        `[documentService] 📁 ❌ ERROR - Failed to get snapshot signed URL:`,
        error,
      );
      console.error(`[documentService] 📁 Error details:`, {
        message: error.message,
        stack: error.stack,
        filePath: cleanPath,
      });
      throw error;
    }
  },

  /**
   * Orchestrates getting the filename and then the signed URL from a chunk ID.
   * @param {string} chunkId - The UUID of the chunk.
   * @param {string} userId - The user ID.
   * @returns {Promise<{filename: string, signedUrl: string}>}
   */
  async getDocumentInfoFromChunk(chunkId, userId) {
    console.log(
      `[documentService] Starting getDocumentInfoFromChunk for chunk ID: ${chunkId}`,
    );

    const filename = await this.getFilenameFromChunk(chunkId, userId);
    if (!filename) {
      console.error(
        `[documentService] Failed to retrieve filename for chunk ID: ${chunkId}`,
      );
      throw new Error("Could not retrieve filename for the specified chunk.");
    }

    console.log(
      `[documentService] Successfully retrieved filename: ${filename}, now getting signed URL...`,
    );
    const signedUrl = await this.getSignedUrl(filename, userId);
    if (!signedUrl) {
      console.error(
        `[documentService] Failed to retrieve signed URL for filename: ${filename}`,
      );
      throw new Error("Could not retrieve signed URL for the document.");
    }

    console.log(
      `[documentService] Successfully completed getDocumentInfoFromChunk - filename: ${filename}, signedUrl: ${signedUrl ? "SUCCESS" : "FAILED"}`,
    );
    return { filename, signedUrl };
  },

  /**
   * Opens a document in a new browser tab, scrolling to the chunk's page (for PDFs).
   * @param {string} chunkId - The UUID of the chunk.
   * @param {string} userId - The user ID.
   * @returns {Promise<void>}
   */
  async openDocumentInNewTab(chunkId, userId) {
    function getAnchorSentences(rawContent) {
      const lines = rawContent
        .split("\n")
        .map(line =>
          line
            .replace(/\*\*/g, "")
            .replace(/\*/g, "")
            .replace(/#{1,6}\s/g, "")
            .replace(/^[●•\-\s\u200b]+/, "")
            .replace(/\s+/g, " ")
            .trim(),
        )
        .filter(line => line.length > 0);

      console.log("All cleaned lines:", lines);

      const realSentences = lines.filter(
        line =>
          line.split(" ").length > 2 &&
          !line.startsWith("http") &&
          !line.startsWith("Page ") &&
          !/^\d+\/\d+\/\d+/.test(line),
      );

      const secondSentence = realSentences[0] ?? ""; // first real sentence
      const secondLastSentence = realSentences[realSentences.length - 1] ?? ""; // last real sentence

      const toShortPhrase = sentence =>
        sentence
          .replace(/[\u200b\u200c\u200d\ufeff]/g, "")
          .split(" ")
          .filter(Boolean)
          .slice(0, 4)
          .join(" ");

      const highlightStart = toShortPhrase(secondSentence);
      const highlightEnd = toShortPhrase(secondLastSentence);

      console.log(
        "************************************** highlightStart:",
        highlightStart,
      );
      console.log(
        "************************************** highlightEnd:",
        highlightEnd,
      );

      return { highlightStart, highlightEnd };
    }

    try {
      const location = await this.getChunkLocation(chunkId, userId);
      const { fileId, source, content, filename } = location;
      if (!fileId) {
        throw new Error("Could not retrieve file ID for the specified chunk.");
      }

      const signedUrl = await this.getSignedUrlForViewing(fileId, userId);
      if (!signedUrl) {
        throw new Error(
          "Could not retrieve signed URL for viewing the document.",
        );
      }

      const firstSegment = source && source[0];
      const pdfPage =
        firstSegment != null &&
        typeof firstSegment.pageIndex === "number" &&
        Number.isFinite(firstSegment.pageIndex)
          ? Math.max(1, firstSegment.pageIndex + 1)
          : null;

      const rawContent = content || "";

      // ── Get anchor sentences ──────────────────────────────────────────────
      const { highlightStart, highlightEnd } = getAnchorSentences(rawContent);

      const viewerPath = "/pdf-viewer";
      const params = new URLSearchParams();
      params.set("url", signedUrl);
      params.set("filename", filename);
      if (pdfPage) params.set("page", String(pdfPage));
      if (highlightStart) params.set("highlightStart", highlightStart); // ✅ fixed
      if (highlightEnd) params.set("highlightEnd", highlightEnd);

      const viewerUrl = `${window.location.origin}${viewerPath}?${params.toString()}`;
      window.open(viewerUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error(
        `[documentService] Failed to open document in new tab:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Opens a document by downloading it.
   * @param {string} chunkId - The UUID of the chunk.
   * @param {string} userId - The user ID.
   * @returns {Promise<void>}
   */
  async downloadDocument(chunkId, userId) {
    console.log(
      `[documentService] Downloading document for chunk ID: ${chunkId}`,
    );

    try {
      const filename = await this.getFilenameFromChunk(chunkId, userId);
      if (!filename) {
        throw new Error("Could not retrieve filename for the specified chunk.");
      }

      // Get signed URL for download
      const signedUrl = await this.getSignedUrl(filename, userId);
      if (!signedUrl) {
        throw new Error(
          "Could not retrieve signed URL for downloading the document.",
        );
      }

      // Trigger download
      console.log(`[documentService] Triggering download for: ${filename}`);
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = filename;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`[documentService] ✓ Download triggered for ${filename}`);
    } catch (error) {
      console.error(`[documentService] Failed to download document:`, error);
      throw error;
    }
  },
};
